import {
  MetricsV1AggregationType,
  MetricsV1FilterInput,
  MetricsV1ValueCapInput,
} from '@spotify/confidence-graphql';

import { aggregationUtils } from '../../Aggregation';
import {
  applyCap,
  coalesceToZeroIf,
  filterToSqlPredicate,
  filterToWhereClause,
} from './sqlHelpers';

export type RatioCodeSnippetProps = {
  entity?: string;
  numerator?: string;
  denominator?: string;
  numeratorAggregation?: MetricsV1AggregationType;
  denominatorAggregation?: MetricsV1AggregationType;
  numeratorCap?: Maybe<MetricsV1ValueCapInput>;
  denominatorCap?: Maybe<MetricsV1ValueCapInput>;
  factTable?: string;
  replaceMissingWithZero?: boolean;
  filter?: Maybe<MetricsV1FilterInput>;
  numeratorFilter?: Maybe<MetricsV1FilterInput>;
};

export const RATIO_KEYS: ReadonlyArray<keyof RatioCodeSnippetProps> = [
  'entity',
  'numerator',
  'denominator',
  'numeratorAggregation',
  'denominatorAggregation',
  'numeratorCap',
  'denominatorCap',
  'factTable',
  'replaceMissingWithZero',
  'filter',
  'numeratorFilter',
];

const buildRatioExprs = ({
  numerator = '*',
  denominator = '*',
  numeratorAggregation = MetricsV1AggregationType.AggregationTypeAvg,
  denominatorAggregation = MetricsV1AggregationType.AggregationTypeAvg,
  numeratorCap,
  denominatorCap,
  numeratorFilter,
}: RatioCodeSnippetProps): { numerator: string; denominator: string } => {
  const numAgg =
    aggregationUtils.getAggregationTypeSqlLabel(numeratorAggregation);
  const denomAgg = aggregationUtils.getAggregationTypeSqlLabel(
    denominatorAggregation,
  );
  const numeratorPredicate = filterToSqlPredicate(numeratorFilter);
  const caseValue = numerator === '*' ? '1' : numerator;
  const numExpr = numeratorPredicate
    ? `${numAgg}(CASE WHEN ${numeratorPredicate} THEN ${caseValue} END)`
    : `${numAgg}(${numerator})`;
  return {
    numerator: applyCap(numExpr, numeratorCap),
    denominator: applyCap(`${denomAgg}(${denominator})`, denominatorCap),
  };
};

const ratioInner = (
  cfg: RatioCodeSnippetProps,
  coalesceNumerator: boolean,
): string => {
  const entity = cfg.entity || 'entity';
  const factTable = cfg.factTable || 'fact_table';
  const exprs = buildRatioExprs(cfg);
  const numerator = coalesceNumerator
    ? coalesceToZeroIf(exprs.numerator, cfg.replaceMissingWithZero)
    : exprs.numerator;

  return `SELECT
    ${entity},
    ${numerator} as numerator,
    ${exprs.denominator} as denominator
  FROM ${factTable}${filterToWhereClause(cfg.filter)}
  GROUP BY ${entity}`;
};

export const ratioCodeSnippet = (
  cfg: RatioCodeSnippetProps = {},
): string => `WITH measurements AS (
  ${ratioInner(cfg, false)}
)

SELECT
  SUM(${coalesceToZeroIf('numerator', cfg.replaceMissingWithZero)}) / SUM(${coalesceToZeroIf(
    'denominator',
    cfg.replaceMissingWithZero,
  )}) as metric
FROM measurements`;

export const ratioMeasurementSnippet = (
  cfg: RatioCodeSnippetProps = {},
): string => ratioInner(cfg, true);
